import { useEffect, useState } from "react";
import Select from "react-select";
import { Formik, Form, Field } from "formik";
import { number, object, string, date } from "yup";
import { toast } from "react-toastify";
import CloseIcon from "../../../assets/hospitalCloseIcon.svg";
import RightArrow from "../../../assets/rightArrowWhite.svg";
import { CaseServices } from "../../../services/CaseServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";

const TPA_WARD_OPTIONS = [
  { label: "ICU", value: "ICU" },
  { label: "Presidential Suite", value: "Presidential Suite" },
  { label: "Deluxe Room", value: "Deluxe Room" },
  { label: "Private Room", value: "Private Room" },
  { label: "Semi Private", value: "Semi Private" },
  { label: "General Ward", value: "General Ward" },
];

const DAYS_OPTIONS = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

const errorValidation = object({
  procedure: string().trim().required("Required Field"),
  ward: string().required("Required Field"),
  days: number().required("Required Field"),
  fromDate: date().required("Required Field"),
  toDate: date().required("Required Field"),
});

/**
 * TPA Enhance step 1 (details) — mirrors admission ReferenceNotUploadedModal.
 * Upload opens a separate modal via onContinueToUpload.
 */
const TpaEnhancementFormHospitalPersona = ({
  setAddEnhancement,
  caseDetails,
  caseId,
  admissionDate,
  enhancementList = [],
  onContinueToUpload,
}) => {
  const [latestEnhancement, setLatestEnhancement] = useState(null);

  const initialValues = {
    procedure: "",
    ward: "",
    days: "",
    fromDate: "",
    toDate: "",
  };

  const handleDaysChange = (item, setFieldValue, values) => {
    setFieldValue("days", item.value);
    if (values.fromDate) {
      const toDate = new Date(values.fromDate);
      toDate.setDate(toDate.getDate() + Number(item.value) - 1);
      setFieldValue("toDate", toDate.toISOString().split("T")[0]);
    }
  };

  const handleDateChange = (e, setFieldValue, values) => {
    const fromDate = e.target.value;
    const toDate = new Date(fromDate);
    toDate.setDate(toDate.getDate() + Number(values.days || 1) - 1);
    setFieldValue("fromDate", fromDate);
    setFieldValue("toDate", toDate.toISOString().split("T")[0]);
  };

  const minFromDate = (() => {
    const rejected =
      caseDetails?.submasterstagestatuses?.[0]?.commandCenterStage
        ?.toLowerCase()
        .includes("enhancement rejected");
    if (rejected) {
      const base =
        enhancementList?.length > 0
          ? new Date(enhancementList[0]?.details?.fromDate || admissionDate)
          : new Date(
              new Date(admissionDate).setDate(
                new Date(admissionDate).getDate() + 1,
              ),
            );
      return base.toISOString().split("T")[0];
    }
    const base =
      latestEnhancement?.length > 0
        ? new Date(
            new Date(latestEnhancement[0]?.details?.toDate).setDate(
              new Date(latestEnhancement[0]?.details?.toDate).getDate() + 1,
            ),
          )
        : new Date(
            new Date(admissionDate).setDate(
              new Date(admissionDate).getDate() + 1,
            ),
          );
    return base.toISOString().split("T")[0];
  })();

  const getLatestCaseStage = async () => {
    try {
      const response = await CaseServices.getLatestCaseStage(caseId);
      if (response?.data?.currnetSubMasterStage?.[0]) {
        setLatestEnhancement(response?.data?.latestEnhancement);
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const handleUploadReferenceClick = async (
    values,
    validateForm,
    setTouched,
  ) => {
    const errors = await validateForm();
    setTouched({
      procedure: true,
      ward: true,
      days: true,
      fromDate: true,
      toDate: true,
    });
    if (Object.keys(errors || {}).length) {
      toast.error("Please fill all required fields");
      return;
    }
    onContinueToUpload?.(values);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    getLatestCaseStage();
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="fixed top-0 left-0 w-screen h-screen flex justify-center items-center bg-[#017C7180] z-[1200]">
      <div className="relative h-fit w-[50%] max-w-[90vw] bg-white p-[30px] rounded-[30px] overflow-y-auto overflow-x-hidden max-h-[90vh] box-border">
        <div
          className="absolute top-1 right-2 cursor-pointer"
          onClick={() => setAddEnhancement(false)}
        >
          <img src={CloseIcon} alt="close-icon" />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={errorValidation}
          onSubmit={() => {}}
        >
          {({
            errors,
            touched,
            setFieldValue,
            values,
            validateForm,
            setTouched,
          }) => (
            <Form>
              <div className="text-[40px] font-medium text-black -tracking-[7%]">
                Enhancement
              </div>
              <div
                style={{ borderTop: "1px solid #969696", marginBlock: "25px" }}
              />

              <div className="w-full flex flex-col gap-y-8">
                <div className="w-full">
                  <div className="text-sm text-[#0B0B0B] leading-[21.7px] -tracking-[3%] mb-4">
                    1. Enter procedure description?
                  </div>
                  <Field
                    type="text"
                    name="procedure"
                    placeholder="ENTER PROCEDURE DESCRIPTION"
                    style={{ border: "1px solid #E4E4E4", width: "100%" }}
                    className="rounded-full py-[10px] px-4 outline-none"
                  />
                  {touched.procedure && errors.procedure && (
                    <div className="field-error">{errors.procedure}</div>
                  )}
                </div>

                <div className="w-full">
                  <div className="text-sm text-[#0B0B0B] leading-[21.7px] -tracking-[3%] mb-4">
                    2. In which ward would you like to take the enhancement?
                  </div>
                  <Select
                    name="ward"
                    placeholder="Select Ward"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "100px",
                      }),
                    }}
                    options={TPA_WARD_OPTIONS}
                    value={
                      TPA_WARD_OPTIONS.find((o) => o.value === values.ward) ||
                      null
                    }
                    onChange={(item) => setFieldValue("ward", item.value)}
                  />
                  {touched.ward && errors.ward && (
                    <div className="field-error">{errors.ward}</div>
                  )}
                </div>

                <div className="w-full">
                  <div className="text-sm text-[#0B0B0B] leading-[21.7px] -tracking-[3%] mb-4">
                    3. For how many days would you like to initiate the
                    enhancement?
                  </div>
                  <Select
                    name="days"
                    placeholder="Select Days"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "100px",
                      }),
                    }}
                    options={DAYS_OPTIONS}
                    value={
                      DAYS_OPTIONS.find(
                        (o) => o.value === String(values.days),
                      ) || null
                    }
                    onChange={(item) =>
                      handleDaysChange(item, setFieldValue, values)
                    }
                  />
                  {touched.days && errors.days && (
                    <div className="field-error">{errors.days}</div>
                  )}
                </div>

                <div className="w-full">
                  <div className="text-sm text-[#0B0B0B] leading-[21.7px] -tracking-[3%] mb-4">
                    4. From which date to which date should the enhancement
                    last?
                  </div>
                  <div className="grid grid-cols-2 gap-4 w-full min-w-0">
                    <div className="min-w-0">
                      <Field
                        type="date"
                        name="fromDate"
                        min={minFromDate}
                        max={new Date().toISOString().split("T")[0]}
                        style={{ border: "1px solid #E4E4E4" }}
                        className="rounded-full py-[10px] px-2 w-full max-w-full box-border"
                        onChange={(e) =>
                          handleDateChange(e, setFieldValue, values)
                        }
                      />
                      {touched.fromDate && errors.fromDate && (
                        <div className="field-error">{errors.fromDate}</div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <Field
                        type="date"
                        name="toDate"
                        disabled
                        style={{ border: "1px solid #E4E4E4" }}
                        className="rounded-full py-[10px] px-2 w-full max-w-full box-border"
                      />
                      {touched.toDate && errors.toDate && (
                        <div className="field-error">{errors.toDate}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-center items-center mt-2">
                  <button
                    type="button"
                    style={{ border: "1px solid #017C71" }}
                    className="flex items-center gap-4 text-sm text-white bg-[#017C71] -tracking-[3%] uppercase rounded-full px-10 py-4 border-0 cursor-pointer"
                    onClick={() =>
                      handleUploadReferenceClick(
                        values,
                        validateForm,
                        setTouched,
                      )
                    }
                  >
                    Upload Reference Document
                    <img src={RightArrow} alt="" />
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default TpaEnhancementFormHospitalPersona;
